// Modelo del formulario de crear y editar promo de venta: esquema zod en espejo de los DTOs de
// type-kit y conversión entre lo que se teclea (pesos, %) y el body (centavos, puntos base).
import type { FieldError, FieldErrors } from 'react-hook-form';
import { z } from 'zod';
import { mxDay, mxDayEndUtc, mxDayStartUtc } from '@/lib/dateHelpers';
import {
    ACCESSORY_SUBCATEGORIES,
    type AccessorySubcategory,
    type CouponScope,
    type CreateSalePromotionRequest,
    type DiscountType,
    type GiftMode,
    type ProductCategory,
    type ProductTier,
    type PromotionPaymentOption,
    type SalePromotion,
    type SalePromotionLeversInput,
} from '../api/promotionsContract';
import { PAYMENT_OPTION_VALUES } from './salePromotionFormOptions';
import { budgetCents, isBudgetAmount } from './budgetText';

const MONEY = /^\d+(\.\d{1,2})?$/;
const WHOLE = /^\d+$/;
// Espejo de MAX_AIRTIME_RECHARGES del back.
const MAX_AIRTIME_RECHARGES = 12;

export const FORM_MESSAGES = {
    name: 'Escribe el nombre de la promo.',
    levers: 'Activa al menos una palanca.',
    money: 'Pon un monto mayor a $0 con hasta dos decimales.',
    percent: 'Pon un porcentaje entre 0.01 y 100.',
    count: 'Pon un número entero de 1 o más.',
    giftSkus: 'Agrega al menos un accesorio a la lista.',
    giftSubcategory: 'Elige la subcategoría de accesorios.',
    validUntil: 'Pon la fecha de fin o marca «Permanente».',
    range: 'El fin no puede ir antes del inicio.',
    rechargeCount: `Pon un número entero entre 1 y ${String(MAX_AIRTIME_RECHARGES)}.`,
} as const;

/** Tope de cuántos elige el cliente: los regalos que hay en la lista. Va junto al campo. */
export const chooseCountMessage = (giftCount: number) =>
    `No puede ser mayor que los regalos de la lista (${String(giftCount)}).`;

const discountType = z.enum(['AMOUNT', 'PERCENTAGE']);
const cappedDiscount = z.object({
    enabled: z.boolean(),
    type: discountType,
    value: z.string(),
    maxAmount: z.string(),
});

export const salePromotionFormSchema = z
    .object({
        name: z.string(),
        customerText: z.string(),
        deviceDiscount: cappedDiscount,
        accessoryDiscount: cappedDiscount,
        gift: z.object({
            enabled: z.boolean(),
            mode: z.enum(['LIST', 'RANGE']),
            skus: z.array(z.string()),
            accessorySubcategory: z.union([z.enum(ACCESSORY_SUBCATEGORIES), z.literal('')]),
            maxPrice: z.string(),
            chooseCount: z.string(),
        }),
        coupon: z.object({
            enabled: z.boolean(),
            type: discountType,
            value: z.string(),
            validityDays: z.string(),
            scope: z.enum(['CHAIN', 'ISSUING_STORE', 'PROMOTION_STORES']),
        }),
        airtime: z.object({
            enabled: z.boolean(),
            rechargeCount: z.string(),
            rechargeAmount: z.string(),
        }),
        walletBonus: z.object({ enabled: z.boolean(), amount: z.string() }),
        segmentIds: z.array(z.string()),
        requiresActiveSurekeepCredit: z.boolean(),
        productCategories: z.array(z.enum(['PHONE', 'SIM', 'MODEM', 'ACCESSORY', 'BUNDLE'])),
        tiers: z.array(z.enum(['LOW', 'MID', 'HIGH', 'PREMIUM'])),
        skus: z.array(z.string()),
        storeIds: z.array(z.string()),
        budgetMax: z.string(),
        validFrom: z.string(),
        permanent: z.boolean(),
        validUntil: z.string(),
        oncePerCustomer: z.boolean(),
        paymentOptions: z.array(z.enum(PAYMENT_OPTION_VALUES)),
        stackable: z.boolean().optional(),
    })
    .superRefine((values, ctx) => {
        for (const [path, message] of formIssues(values)) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path, message });
        }
    });

/** Descuento en $ o % con tope opcional; `maxAmount` vacío = sin tope. */
export interface CappedDiscountForm {
    enabled: boolean;
    type: DiscountType;
    value: string;
    maxAmount: string;
}

export interface SalePromotionFormValues {
    name: string;
    customerText: string;
    deviceDiscount: CappedDiscountForm;
    accessoryDiscount: CappedDiscountForm;
    gift: {
        enabled: boolean;
        mode: GiftMode;
        skus: string[];
        /** Vacío = sin elegir. */
        accessorySubcategory: AccessorySubcategory | '';
        maxPrice: string;
        chooseCount: string;
    };
    coupon: {
        enabled: boolean;
        type: DiscountType;
        value: string;
        validityDays: string;
        scope: CouponScope;
    };
    airtime: { enabled: boolean; rechargeCount: string; rechargeAmount: string };
    walletBonus: { enabled: boolean; amount: string };
    segmentIds: string[];
    requiresActiveSurekeepCredit: boolean;
    productCategories: ProductCategory[];
    tiers: ProductTier[];
    skus: string[];
    storeIds: string[];
    /** Vacío = sin tope. */
    budgetMax: string;
    /** Día CDMX `YYYY-MM-DD`; vacío = sin inicio. */
    validFrom: string;
    permanent: boolean;
    validUntil: string;
    oncePerCustomer: boolean;
    /** Formas de pago con las que aplica; vacío = cualquiera. */
    paymentOptions: PromotionPaymentOption[];
    /** Acumulable; sin tocar vale lo de `defaultStackable`. */
    stackable?: boolean;
}

export type Issue = readonly [path: (string | number)[], message: string];

export const isMoney = (text: string) => MONEY.test(text.trim()) && Number(text) > 0;
const isPercent = (text: string) => isMoney(text) && Number(text) <= 100;
export const isCount = (text: string) => WHOLE.test(text.trim()) && Number(text) >= 1;

export function discountIssue(type: DiscountType, value: string, path: string[]): Issue | null {
    if (type === 'PERCENTAGE') return isPercent(value) ? null : [path, FORM_MESSAGES.percent];
    return isMoney(value) ? null : [path, FORM_MESSAGES.money];
}

function leverIssues(v: SalePromotionFormValues): (Issue | null)[] {
    const out: (Issue | null)[] = [];
    const check = (ok: boolean, path: string[], message: string) => {
        if (!ok) out.push([path, message]);
    };
    for (const lever of ['deviceDiscount', 'accessoryDiscount'] as const) {
        const discount = v[lever];
        if (!discount.enabled) continue;
        out.push(discountIssue(discount.type, discount.value, [lever, 'value']));
        const max = discount.maxAmount;
        check(max.trim() === '' || isMoney(max), [lever, 'maxAmount'], FORM_MESSAGES.money);
    }
    if (v.gift.enabled) {
        if (v.gift.mode === 'LIST')
            check(v.gift.skus.length > 0, ['gift', 'skus'], FORM_MESSAGES.giftSkus);
        else {
            check(
                v.gift.accessorySubcategory !== '',
                ['gift', 'accessorySubcategory'],
                FORM_MESSAGES.giftSubcategory,
            );
            check(isMoney(v.gift.maxPrice), ['gift', 'maxPrice'], FORM_MESSAGES.money);
        }
        const listSize = v.gift.skus.length;
        if (!isCount(v.gift.chooseCount)) out.push([['gift', 'chooseCount'], FORM_MESSAGES.count]);
        else if (v.gift.mode === 'LIST' && listSize > 0 && Number(v.gift.chooseCount) > listSize)
            out.push([['gift', 'chooseCount'], chooseCountMessage(listSize)]);
    }
    if (v.coupon.enabled) {
        out.push(discountIssue(v.coupon.type, v.coupon.value, ['coupon', 'value']));
        check(isCount(v.coupon.validityDays), ['coupon', 'validityDays'], FORM_MESSAGES.count);
    }
    if (v.airtime.enabled) {
        check(
            isCount(v.airtime.rechargeCount) &&
                Number(v.airtime.rechargeCount) <= MAX_AIRTIME_RECHARGES,
            ['airtime', 'rechargeCount'],
            FORM_MESSAGES.rechargeCount,
        );
        check(
            isMoney(v.airtime.rechargeAmount),
            ['airtime', 'rechargeAmount'],
            FORM_MESSAGES.money,
        );
    }
    if (v.walletBonus.enabled)
        check(isMoney(v.walletBonus.amount), ['walletBonus', 'amount'], FORM_MESSAGES.money);
    return out;
}

function formIssues(v: SalePromotionFormValues): Issue[] {
    const issues: (Issue | null)[] = [];
    if (v.name.trim() === '') issues.push([['name'], FORM_MESSAGES.name]);
    if (!LEVER_KEYS.some((key) => v[key].enabled)) issues.push([['levers'], FORM_MESSAGES.levers]);
    issues.push(...leverIssues(v));
    if (!isBudgetAmount(v.budgetMax)) issues.push([['budgetMax'], FORM_MESSAGES.money]);
    if (!v.permanent && v.validUntil === '')
        issues.push([['validUntil'], FORM_MESSAGES.validUntil]);
    else if (!v.permanent && v.validFrom !== '' && v.validUntil < v.validFrom)
        issues.push([['validUntil'], FORM_MESSAGES.range]);
    return issues.filter((issue): issue is Issue => issue !== null);
}

/** Las palancas en el orden del formulario. */
export const LEVER_KEYS = [
    'deviceDiscount',
    'accessoryDiscount',
    'gift',
    'coupon',
    'airtime',
    'walletBonus',
] as const;

export function emptySalePromotionForm(): SalePromotionFormValues {
    return {
        name: '',
        customerText: '',
        deviceDiscount: { enabled: false, type: 'AMOUNT', value: '', maxAmount: '' },
        accessoryDiscount: { enabled: false, type: 'PERCENTAGE', value: '', maxAmount: '' },
        gift: {
            enabled: false,
            mode: 'LIST',
            skus: [],
            accessorySubcategory: '',
            maxPrice: '',
            chooseCount: '1',
        },
        coupon: { enabled: false, type: 'AMOUNT', value: '', validityDays: '30', scope: 'CHAIN' },
        airtime: { enabled: false, rechargeCount: '1', rechargeAmount: '' },
        walletBonus: { enabled: false, amount: '' },
        segmentIds: [],
        requiresActiveSurekeepCredit: false,
        productCategories: [],
        tiers: [],
        skus: [],
        storeIds: [],
        budgetMax: '',
        validFrom: '',
        permanent: false,
        validUntil: '',
        oncePerCustomer: false,
        paymentOptions: ['SUREKEEP'],
    };
}

export const toCents = (pesos: string) => Math.round(Number(pesos) * 100);
const toBps = (percent: string) => Math.round(Number(percent) * 100);
export const fromHundredths = (value: number) => String(value / 100);
const listOrNull = <T>(list: T[]): T[] | null => (list.length > 0 ? list : null);

export function discountValue(type: DiscountType, value: string) {
    return type === 'AMOUNT'
        ? { type, amountCents: toCents(value), percentBps: null }
        : { type, amountCents: null, percentBps: toBps(value) };
}

function cappedDiscountValue(discount: CappedDiscountForm) {
    const max = discount.maxAmount.trim();
    return {
        ...discountValue(discount.type, discount.value),
        maxAmountCents: max === '' ? null : toCents(max),
    };
}

function toLevers(v: SalePromotionFormValues): SalePromotionLeversInput {
    const { deviceDiscount, accessoryDiscount, gift, coupon, airtime } = v;
    const isList = gift.mode === 'LIST';
    return {
        deviceDiscount: deviceDiscount.enabled ? cappedDiscountValue(deviceDiscount) : null,
        accessoryDiscount: accessoryDiscount.enabled
            ? cappedDiscountValue(accessoryDiscount)
            : null,
        gift: gift.enabled
            ? {
                  mode: gift.mode,
                  skus: isList ? gift.skus : null,
                  accessorySubcategory:
                      isList || gift.accessorySubcategory === '' ? null : gift.accessorySubcategory,
                  maxPriceCents: isList ? null : toCents(gift.maxPrice),
                  chooseCount: Number(gift.chooseCount),
              }
            : null,
        coupon: coupon.enabled
            ? {
                  ...discountValue(coupon.type, coupon.value),
                  validityDays: Number(coupon.validityDays),
                  scope: coupon.scope,
              }
            : null,
        airtime: airtime.enabled
            ? {
                  rechargeCount: Number(airtime.rechargeCount),
                  rechargeAmountCents: toCents(airtime.rechargeAmount),
              }
            : null,
        // Las promos de venta ya no dan garantía; en null el parche se la quita a las viejas.
        extendedWarranty: null,
        walletBonus: v.walletBonus.enabled ? { amountCents: toCents(v.walletBonus.amount) } : null,
    };
}

/** Body completo; editar manda el mismo body como parche. */
export function toCreateSalePromotionRequest(
    v: SalePromotionFormValues,
): CreateSalePromotionRequest {
    return {
        name: v.name.trim(),
        customerText: v.customerText.trim(),
        levers: toLevers(v),
        scope: {
            productCategories: listOrNull(v.productCategories),
            tiers: listOrNull(v.tiers),
            skus: listOrNull(v.skus),
            storeIds: listOrNull(v.storeIds),
        },
        segmentation: {
            segmentIds: listOrNull(v.segmentIds),
            requiresActiveSurekeepCredit: v.requiresActiveSurekeepCredit,
        },
        paymentOptions: listOrNull(v.paymentOptions),
        budgetMaxCents: budgetCents(v.budgetMax),
        validFrom: v.validFrom === '' ? null : mxDayStartUtc(v.validFrom),
        validUntil: v.permanent || v.validUntil === '' ? null : mxDayEndUtc(v.validUntil),
        oncePerCustomer: v.oncePerCustomer,
        stackable: v.stackable ?? defaultStackable(v),
    };
}

/** Sin tocar la casilla: acumulable solo si la promo es solo de regalo. */
export function defaultStackable(
    v: Pick<SalePromotionFormValues, (typeof LEVER_KEYS)[number]>,
): boolean {
    return v.gift.enabled && LEVER_KEYS.every((key) => key === 'gift' || !v[key].enabled);
}

/** El monto en pesos o el porcentaje como se teclea en el formulario. */
export function discountText(value: {
    type: DiscountType;
    amountCents: number | null;
    percentBps: number | null;
}) {
    const raw = value.type === 'AMOUNT' ? value.amountCents : value.percentBps;
    return raw === null ? '' : fromHundredths(raw);
}

/** Lo que guarda el back de una palanca con tope; las promos viejas de accesorios no traen `type`. */
interface StoredCappedDiscount {
    type?: DiscountType | null;
    amountCents?: number | null;
    percentBps?: number | null;
    maxAmountCents?: number | null;
}

function cappedDiscountForm(stored: StoredCappedDiscount): CappedDiscountForm {
    const type = stored.type ?? 'PERCENTAGE';
    const max = stored.maxAmountCents ?? null;
    return {
        enabled: true,
        type,
        value: discountText({
            type,
            amountCents: stored.amountCents ?? null,
            percentBps: stored.percentBps ?? null,
        }),
        maxAmount: max === null ? '' : fromHundredths(max),
    };
}

export function formValuesFromPromotion(promo: SalePromotion): SalePromotionFormValues {
    const empty = emptySalePromotionForm();
    const { deviceDiscount, accessoryDiscount, gift, coupon, airtime, walletBonus } = promo.levers;
    return {
        ...empty,
        name: promo.name,
        customerText: promo.customerText,
        deviceDiscount: deviceDiscount ? cappedDiscountForm(deviceDiscount) : empty.deviceDiscount,
        accessoryDiscount: accessoryDiscount
            ? cappedDiscountForm(accessoryDiscount)
            : empty.accessoryDiscount,
        gift: gift
            ? {
                  enabled: true,
                  mode: gift.mode,
                  skus: gift.skus ?? [],
                  accessorySubcategory: gift.accessorySubcategory ?? '',
                  maxPrice: gift.maxPriceCents === null ? '' : fromHundredths(gift.maxPriceCents),
                  chooseCount: String(gift.chooseCount),
              }
            : empty.gift,
        coupon: coupon
            ? {
                  enabled: true,
                  type: coupon.type,
                  value: discountText(coupon),
                  validityDays: String(coupon.validityDays),
                  scope: coupon.scope,
              }
            : empty.coupon,
        airtime: airtime
            ? {
                  enabled: true,
                  rechargeCount: String(airtime.rechargeCount),
                  rechargeAmount: fromHundredths(airtime.rechargeAmountCents),
              }
            : empty.airtime,
        walletBonus: walletBonus
            ? { enabled: true, amount: fromHundredths(walletBonus.amountCents) }
            : empty.walletBonus,
        segmentIds: promo.segmentation.segmentIds ?? [],
        requiresActiveSurekeepCredit: promo.segmentation.requiresActiveSurekeepCredit,
        productCategories: promo.scope.productCategories ?? [],
        tiers: promo.scope.tiers ?? [],
        skus: promo.scope.skus ?? [],
        storeIds: promo.scope.storeIds ?? [],
        budgetMax: promo.budgetMaxCents === null ? '' : fromHundredths(promo.budgetMaxCents),
        validFrom: promo.validFrom === null ? '' : mxDay(promo.validFrom),
        permanent: promo.validUntil === null,
        validUntil: promo.validUntil === null ? '' : mxDay(promo.validUntil),
        oncePerCustomer: promo.oncePerCustomer,
        paymentOptions: promo.paymentOptions ?? [],
        stackable: promo.stackable,
    };
}

/** El error de «ninguna palanca» no cuelga de un campo: viaja en `levers`. */
export function leversError(errors: FieldErrors<SalePromotionFormValues>): string | undefined {
    return (errors as { levers?: FieldError }).levers?.message;
}
